import { useCounters } from 'cityads-v3/env';
import { Dispatch } from 'redux'

import {
  SMS_RESULT_NO_ATTEMPTS_LEFT,
  SMS_RESULT_WRONG_CODE,
  SMS_RESULT_PHONE_NOT_FOUND,
  SMS_RESULT_ERROR,
} from 'cityads-v3/utils/smsUtils';

declare global {
  interface Window {
    ga(set: string, page: string, url: string, action?: string, ...rest: any): void,
    dataLayer: Array<unknown>,
    mindbox(type: string, options: {
      operation: string
      data: unknown
    }): void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [index: string]: any
  }
}

type TYandexCounter = {
  hit(url: string, options: unknown): void
  reachGoal(target: string, params: unknown, ...rest: unknown[]): void
}

export const yandexCounterName = 'yaCounter45735810';
export function yandexHit(url: string, options: unknown): void {
  if (!useCounters) { return; }

  const counter: TYandexCounter = window && window[yandexCounterName];

  if (counter) {
    counter.hit(url, options);
  }
}

export function yandexReachGoal(target: string, params?: unknown | string, ...rest: unknown[]): void {
  if (!useCounters) { return; }

  const counter: TYandexCounter = window && window[yandexCounterName];

  if (counter) {
    counter.reachGoal(target, params, ...rest);
  }
}

export function googleHit(url: string): void {
  if (!useCounters) { return; }

  const ga = window && window.ga;

  if (ga) {
    ga('set', 'page', url);
  }
}

export function googleEvent(category: string, action: string, ...rest: unknown[]): void {
  if (!useCounters) { return; }

  const ga = window && window.ga;

  if (ga) {
    ga('send', 'event', category, action, ...rest);
  }
}

export function mindboxNotify(operation: string, data: unknown): void {
  if (!useCounters) { return; }

  const mindbox = window && window.mindbox;

  if (mindbox) {
    mindbox('async', {
      operation: operation,
      data: data,
    });
  }
}

export function mindboxNotifyAddProductToList(operation: string, offerId: number) {
  return (dispatch: Dispatch<any>, getState: any): void => {
    const userId = getState().auth.user?.id;

    googleEvent('Offers', operation, offerId);
    yandexReachGoal(operation, { offerId });

    mindboxNotify(operation, {
      customer: {
        ids: {
          cityAdsID: userId
        }
      },
      addProductToList: {
        product: {
          ids: {
            cityAds: String(offerId)
          }
        }
      }
    });
  };
}

export function mindboxNotifyViewProduct(operation: string, offerId: number) {
  return (dispatch: Dispatch<any>, getState: any): void => {
    const userId = getState().auth.user?.id;

    googleEvent('Offers', operation, offerId);
    yandexReachGoal(operation, { offerId });

    mindboxNotify(operation, {
      customer: {
        ids: {
          cityAdsID: userId
        }
      },
      viewProduct: {
        product: {
          ids: {
            cityAds: String(offerId)
          }
        }
      }
    });
  };
}

export function gtmPush(data: unknown): void {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(data);
}

type TAmplitudeEvent = 'makeActions' | 'makeConversions';
type TAmplitudeCat = 'auth' | 'registration' | 'welcome_form' | 'verification' | 'offers' | 'payment'
  | 'tools' | 'goods' | 'statistics' | 'help_button' | 'userdocs' | 'firano' | 'office';

interface ILayerEvent {
  event: 'addEvents_makeActions' | 'addEvents_makeConversions' | string,
  event_id: string,
  event_cat: TAmplitudeCat,
  event_name: string,
  [index: string]: unknown
}

export function amplitudeSendEvent(event: TAmplitudeEvent, eventId: string, eventCat: TAmplitudeCat, eventName: string, params: any = {}): void {
  const layerEvent: ILayerEvent = {
    event: `addEvents_${event}`,
    event_id: eventId,
    event_cat: eventCat,
    event_name: eventName
  };

  params.design_version = 'new';

  const paramValues: string[] = [];
  const paramKeys: string[] = Object.keys(params);

  paramKeys.forEach(key => {
    const value = params[key];

    if (Array.isArray(value)) {
      paramValues.push(value.join(', '));
    } else {
      paramValues.push(value);
    }

    layerEvent[key] = JSON.stringify(value);
  });

  layerEvent.event_param = paramValues.join('_');

  gtmPush(layerEvent);
}

export const ERROR_CODE_SMS_SEND_ERROR = 'smsSendError';
export const ERROR_CODE_NO_PHONE = 'noContactPhone';
export const ERROR_CODE_WRONG_SMS_CODE = 'wrongSmsCode';
export const ERROR_CODE_SMS_SEND_NETWORK_OR_SERVER_ERROR = 'smsSendNetworkOrServerError';
export const ERROR_CODE_SMS_CODE_CHECK_NETWORK_OR_SERVER_ERROR = 'smsCodeCheckNetworkOrServerError';

type IAmplitudeErrorTypes = {
  [SMS_RESULT_NO_ATTEMPTS_LEFT]: typeof ERROR_CODE_WRONG_SMS_CODE,
  [SMS_RESULT_WRONG_CODE]: typeof ERROR_CODE_WRONG_SMS_CODE,
  [SMS_RESULT_PHONE_NOT_FOUND]: typeof ERROR_CODE_NO_PHONE,
  [SMS_RESULT_ERROR]: typeof ERROR_CODE_SMS_SEND_ERROR,
  [ERROR_CODE_SMS_SEND_NETWORK_OR_SERVER_ERROR]: typeof ERROR_CODE_SMS_SEND_NETWORK_OR_SERVER_ERROR,
  [ERROR_CODE_SMS_CODE_CHECK_NETWORK_OR_SERVER_ERROR]: typeof ERROR_CODE_SMS_CODE_CHECK_NETWORK_OR_SERVER_ERROR,
  [index: string]: string
}

const ERROR_TYPES:IAmplitudeErrorTypes = {
  [SMS_RESULT_NO_ATTEMPTS_LEFT]: ERROR_CODE_WRONG_SMS_CODE,
  [SMS_RESULT_WRONG_CODE]: ERROR_CODE_WRONG_SMS_CODE,
  [SMS_RESULT_PHONE_NOT_FOUND]: ERROR_CODE_NO_PHONE,
  [SMS_RESULT_ERROR]: ERROR_CODE_SMS_SEND_ERROR,

  [ERROR_CODE_SMS_SEND_NETWORK_OR_SERVER_ERROR]: ERROR_CODE_SMS_SEND_NETWORK_OR_SERVER_ERROR,
  [ERROR_CODE_SMS_CODE_CHECK_NETWORK_OR_SERVER_ERROR]: ERROR_CODE_SMS_CODE_CHECK_NETWORK_OR_SERVER_ERROR,
};

export function amplitudeSendSmsError(isPhoneConfirmed: boolean, errorCode: string | number): void {
  const errorType = ERROR_TYPES[errorCode];

  if (!errorType) { return; }

  amplitudeSendEvent('makeConversions', 'd-v1-e48', 'verification', 'sms_error', {
    error_type: errorType,
    form_type: isPhoneConfirmed ? 'sms verification' : 'account verification'
  });
}
